import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, Check, X } from 'lucide-react';

// The searchable multi-select used by the filter bars on Affiliate Recruitment
// and Affiliate Directory (each still carries its own copy of this; this file
// is the one new pages should import). Options are strings or
// { name, value, ...anything renderOption wants }; the chips under the box
// show the selected values and remove on click.
//
// compact: the toolbar form — no label above, the search-box height, the
// label as the placeholder ("Countries") and the pick summarised in the box
// ("United States", "3 countries") with an X to clear, instead of chips under
// it that would push a toolbar row taller.
const MultiSelectFilter = ({ label, options, selectedValues, onChange, renderOption = null, disabled = false, hint = null, compact = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const filteredOptions = options.filter(option => {
    const searchText = typeof option === 'string' ? option : (option.name || option.value || '');
    return searchText.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const toggleOption = (option) => {
    const value = typeof option === 'string' ? option : (option.value || option.name);
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const getDisplayText = () => {
    if (selectedValues.length === 0) return compact ? label : `Select ${label}...`;
    if (selectedValues.length === 1) return selectedValues[0];
    return compact ? `${selectedValues.length} ${label.toLowerCase()}` : `${selectedValues.length} selected`;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={compact ? 'relative w-56' : 'relative'} ref={dropdownRef}>
      {!compact && (
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-100 mb-2">{label}</label>
      )}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between ${compact ? 'px-3 py-2' : 'px-4 py-3'} bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
          disabled
            ? 'opacity-60 cursor-not-allowed bg-gray-50 dark:bg-slate-900'
            : `hover:border-blue-300 ${isOpen ? 'border-blue-500 ring-2 ring-blue-500 shadow-lg' : 'hover:shadow-md'}`
        }`}
      >
        <span className={`text-sm truncate ${
          selectedValues.length === 0 ? 'text-gray-500 dark:text-slate-400' : 'text-gray-900 dark:text-slate-100'
        }`}>
          {disabled && hint ? hint : getDisplayText()}
        </span>
        <span className="flex items-center flex-shrink-0 ml-2">
          {compact && selectedValues.length > 0 && !disabled && (
            // A span, not a button: this sits inside the toggle button.
            <span
              role="button"
              tabIndex={0}
              aria-label={`Clear ${label}`}
              onClick={(e) => { e.stopPropagation(); onChange([]); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onChange([]); } }}
              className="mr-1 p-0.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {isOpen && !disabled && (
        <div className={`absolute z-50 ${compact ? 'right-0 w-72' : 'w-full'} mt-1 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg shadow-xl animate-in slide-in-from-top-2 duration-200`}>
          <div className="p-3 border-b border-gray-200 dark:border-slate-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 dark:text-slate-400">No options found</div>
            ) : (
              filteredOptions.map((option, index) => {
                const value = typeof option === 'string' ? option : (option.value || option.name);
                const isSelected = selectedValues.includes(value);

                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => toggleOption(option)}
                    className={`w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-blue-50 dark:hover:bg-slate-800 transition-colors duration-150 text-left ${
                      isSelected ? 'bg-blue-50 dark:bg-slate-800 text-blue-700 dark:text-blue-400' : 'text-gray-700 dark:text-slate-100'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {renderOption ? renderOption(option) : (
                        <span className="truncate">
                          {typeof option === 'string' ? option : (option.name || option.value)}
                        </span>
                      )}
                    </div>
                    {isSelected && (
                      <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}

      {!compact && selectedValues.length > 0 && !disabled && (
        <div className="mt-2 flex flex-wrap gap-1">
          {selectedValues.map((value, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md text-xs animate-in slide-in-from-left-2 duration-200"
            >
              <span className="truncate max-w-24">{value}</span>
              <button
                type="button"
                onClick={() => onChange(selectedValues.filter(v => v !== value))}
                className="hover:bg-blue-200 dark:hover:bg-blue-800/50 rounded-full p-0.5 transition-colors flex-shrink-0"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiSelectFilter;
